import { Component, Prop, State, h, Element } from '@stencil/core';
import { BreadcrumbItem } from './interfaces';
import KeyBoardArrowLeftIcon from "../../assets/svg/keyboard_arrow_left.svg"
import KeyBoardArrowRightIcon from "../../assets/svg/keyboard_arrow_right.svg"

/**
 * @name SallaBreadcrumb
 * @description A StencilJS component for rendering breadcrumb navigation.
 * @tag salla-breadcrumb
 *
 * @slot item - Replaces breadcrumb item, has replaceable props `{url}`, `{title}`.
 * @slot icon - Replaces breadcrumb arrow icon.
 */
@Component({
  tag: 'salla-breadcrumb',
  styleUrl: 'salla-breadcrumb.scss',
})
export class SallaBreadcrumb {
  constructor() {
    salla.onReady(() => {
      this.isRtl = salla.config.get('theme.is_rtl', true);
      this.currentPage = salla.config.get('page');
    });

    // Initialize slot HTML or use default if not provided
    this.itemSlot = this.host.querySelector('[slot="item"]')?.outerHTML || '<li class="s-breadcrumb-item"><a href={url}>{title}</a></li>';
    this.iconSlot = this.host.querySelector('[slot="icon"]')?.outerHTML
  }

  @Element() host: HTMLElement;

  /**
   * Breadcrumb items in array
   */
  @Prop() items: string;

  private readonly itemSlot: string;
  private readonly iconSlot: string

  @State() breadcrumbs: BreadcrumbItem[];
  @State() currentPage = salla.config.get('page');
  @State() isRtl: boolean = salla.config.get('theme.is_rtl', true);

  /**
   * Checks if the given index is the last index in the breadcrumbs array.
   * @param index - The index to check.
   * @returns True if the index is the last, false otherwise.
   */
  private isLast(index: number): boolean {
    return index === this.breadcrumbs.length - 1;
  }

  private async getBreadcrumbs() {
    try {
      let params = {
        id: this.currentPage.id,
        page: this.currentPage.slug
      }
      if (this.currentPage.slug == 'product.single') {
        params['parent_id'] = this.currentPage.product_id;
      }
      let resp = await salla.api.landing.fetchBreadcrumbs(params);
      this.breadcrumbs = resp.data[0];
    } catch (error) {
      salla.error("Error fetching breadcrumbs");
      this.breadcrumbs = [];
    }

  }

  /**
   * Renders the breadcrumb component.
   * @returns JSX for rendering breadcrumbs.
   */
  render() {
    return (
      <ol class="s-breadcrumb-wrapper">
        {!!this.breadcrumbs ? this.breadcrumbs.map((item, index) => (
          !this.isLast(index) ? (
            [<div id="breadcrumb-slot" innerHTML={this.itemSlot
              .replace(/\{url\}/g, item.url)
              .replace(/\{title\}/g, item.title)
            }>
            </div>,
            <li class="s-breadcrumb-arrow">
              <div id='breadcrumb-icon-slot' class={{ "s-breadcrumb-default-icon": !this.iconSlot }} innerHTML={this.iconSlot ? this.iconSlot : this.isRtl ? KeyBoardArrowLeftIcon : KeyBoardArrowRightIcon}></div>
            </li>
            ]
          ) : (
            <div id="breadcrumb-slot" innerHTML={this.itemSlot
              .replace(/\{url\}/g, item.url)
              .replace(/\{title\}/g, item.title)
            }>
            </div>
          )
        )) : ''}
      </ol>
    );
  }

  /**
   * Lifecycle method called before the component is loaded.
   * Parses the items string into breadcrumb items.
   */
  async componentWillLoad() {
    await this.getBreadcrumbs();
    try {
      // this.breadcrumbs = JSON.parse(this.items);

    } catch (error) {
      console.error(error);
      this.breadcrumbs = [];
    }
  }

  /**
   * Lifecycle method called after the component is rendered.
   * - Reduces the number of elements in the DOM.
   * - Removes unnecessary slots parent elements.
   * - Replaces the last anchor tag in the breadcrumb with its content.
   */
  componentDidRender() {
    // Reduces the number of elements in the DOM
    this.host.querySelectorAll('#breadcrumb-slot').forEach(el => el.replaceWith(el.firstChild));
    this.host.querySelectorAll('#breadcrumb-icon-slot.s-breadcrumb-default-icon').forEach(el => el.replaceWith(el.querySelector('svg')));

    // Removes the slots parent elements if exists
    this.host.querySelector('[slot="item"]')?.remove();
    this.host.querySelector('[slot="icon"]')?.remove();

    // Removes the last anchor tag in the breadcrumb
    this.host.querySelectorAll('.s-breadcrumb-item').forEach((el, index) => {
      if (index === this.breadcrumbs.length - 1) {
        el.querySelector('a').replaceWith(el.querySelector('a').firstChild);
      }
    });
  }
}
